import { useCallback, useEffect, useState } from "react";
import { Memory } from "../../../api/generated";
import {
    addCommentOnMemory,
    getMemory,
    getSignedUrlForCommentImages,
    addMemoryFavorite,
    removeMemoryFavorite,
    incrementMemoryLikedCount,
} from "../../../api/memory";
import { useAppState, useSetAppState } from "../../../stores/appContext";
import { useAuth } from "../../auth/hooks/useAuth";
import { Path } from "../../../routes/PagePath";
import { showToast } from "@twioku/common_components";
import { isRecordStringString } from "../../../libs/helper";
import { useUploadFileBySignedUrl } from "../../../utils/hooks/useUploadFileBySignedUrl";
import { COMMON_ERROR_MESSAGE } from "../../../libs/constants";
import { useMemoriesCache } from "./useMemoriesCache";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CacheKey } from "../../../constants/CacheKey";

const initialMemory: Memory = {
    userId: "",
    id: "",
    answererId: "",
    answererName: "",
    respondDate: "",
    questionId: "",
    title: "",
    category: "",
    sendDate: "",
    respondContent: "",
    likedCount: 0,
    comments: [],
    isUnread: false,
    hasUnreadComment: false,
    isFavorite: false,
    createAt: "",
    responseStatus: "NOT_ANSWERED_YET",
    explanation: "",
};

type AddFavoriteResult = "success" | "already-favorited" | "pending" | "failed";

// FIXME: 用途ごとに hook を分割し、且つ tanstack query を使う
export const useMemory = (id: string) => {
    const { csrfToken } = useAuth();
    const { replaceMemoryInCache } = useMemoriesCache();
    const queryClient = useQueryClient();
    const [memory, setMemory] = useState<Memory>(initialMemory);
    const [newCommentText, setNewCommentText] = useState<string>("");
    const [newCommentImages, setNewCommentImages] = useState<Array<File>>([]);
    const setAppState = useSetAppState();
    const state = useAppState();
    const { tempLikedMemoryIds } = state;
    const [isLoadingAddComment, setIsLoadingAddComment] =
        useState<boolean>(false);
    const [isMemoryLoading, setIsMemoryLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const uploadImageBySignedUrl = useUploadFileBySignedUrl();
    const addFavoriteMutation = useMutation({
        mutationFn: () => addMemoryFavorite(memory.id, csrfToken),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: CacheKey.favoriteMemories,
            });
        },
        onError: () => {
            setMemory((prevMemory) => ({
                ...prevMemory,
                isFavorite: false,
            }));
        },
    });
    const removeFavoriteMutation = useMutation({
        mutationFn: () => removeMemoryFavorite(memory.id, csrfToken),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: CacheKey.favoriteMemories,
            });
        },
        onError: (error) => {
            setMemory((prevMemory) => ({
                ...prevMemory,
                isFavorite: true,
            }));
            showToast(COMMON_ERROR_MESSAGE, "error");
            console.error(error);
        },
    });

    useEffect(() => {
        if (!id || id.length <= 0) return;
        setIsMemoryLoading(true);
        getMemory(id)
            .then((memory) => {
                setMemory(memory);
            })
            .catch(() => {
                window.location.href = Path.notFound;
            })
            .finally(() => {
                setIsMemoryLoading(false);
            });
    }, [id]);

    useEffect(() => {
        if (!isError) return;

        const timer = setTimeout(() => {
            setIsError(false);
            setErrorMessage("");
        }, 5 * 1000);

        return () => clearTimeout(timer);
    }, [isError, errorMessage]);

    const uploadImage = useCallback(
        async (uploadImage: File[], csrfToken: string) => {
            if (uploadImage.length === 0) return [];

            const signedUrlResults = await getSignedUrlForCommentImages(
                uploadImage,
                csrfToken
            );

            await Promise.all(
                signedUrlResults.map(async (signedUrlResult) => {
                    const file = uploadImage.find(
                        (image) => image.name === signedUrlResult.fileName
                    );
                    if (!file) return;
                    if (!isRecordStringString(signedUrlResult.fields)) return;
                    await uploadImageBySignedUrl(
                        file,
                        signedUrlResult.fields,
                        signedUrlResult.signedUrl
                    );
                })
            );

            return signedUrlResults.map((signedUrl) => signedUrl.filePath);
        },
        [uploadImageBySignedUrl]
    );

    const addComment = async (): Promise<Memory | void> => {
        if (newCommentText.length <= 0 && newCommentImages.length <= 0) return;

        setIsLoadingAddComment(true);

        try {
            const filePaths = await uploadImage(newCommentImages, csrfToken);
            const memory = await addCommentOnMemory(
                id,
                newCommentText,
                filePaths,
                csrfToken
            );
            setMemory(memory);
            replaceMemoryInCache(memory);
            setNewCommentText("");
            setCommentImages([]);
            return memory;
        } catch (error) {
            showToast(COMMON_ERROR_MESSAGE, "error");
            console.error(error);
        } finally {
            setIsLoadingAddComment(false);
        }
    };

    const incrementLikedCount = async () => {
        if (tempLikedMemoryIds.includes(memory.id)) return;
        // いいね数を処理終了を待たずにインクリメントする。
        setMemory((prevMemory) => {
            ++prevMemory.likedCount;
            replaceMemoryInCache({ ...prevMemory });
            return { ...prevMemory };
        });
        setAppState((state) => ({
            ...state,
            tempLikedMemoryIds: [...state.tempLikedMemoryIds, memory.id],
        }));

        incrementMemoryLikedCount(memory.id, csrfToken)
            .then((memory) => {
                setMemory(memory);
                replaceMemoryInCache(memory);
            })
            .catch(() => {
                // 異常終了の場合、いいねインクリメント処理実行前の状態に戻す。
                --memory.likedCount;
                setMemory({ ...memory });
                replaceMemoryInCache({ ...memory });
                setAppState((prevState) => {
                    const newTempLikedMemoryIds =
                        prevState.tempLikedMemoryIds.filter(
                            (n) => n !== memory.id
                        );
                    return {
                        ...prevState,
                        tempLikedMemoryIds: [...newTempLikedMemoryIds],
                    };
                });
                showToast(COMMON_ERROR_MESSAGE, "error");
            });
    };

    const addFavorite = async (): Promise<AddFavoriteResult> => {
        if (memory.isFavorite) return "already-favorited";
        if (addFavoriteMutation.isPending) return "pending";

        setMemory((prevMemory) => ({
            ...prevMemory,
            isFavorite: true,
        }));

        try {
            await addFavoriteMutation.mutateAsync();
            return "success";
        } catch (error) {
            showToast(COMMON_ERROR_MESSAGE, "error");
            console.error(error);
            return "failed";
        }
    };

    const removeFavorite = async () => {
        if (!memory.isFavorite) return;
        if (removeFavoriteMutation.isPending) return;

        setMemory((prevMemory) => ({
            ...prevMemory,
            isFavorite: false,
        }));

        removeFavoriteMutation.mutate();
    };

    const setCommentImages = (
        files: Array<File>
    ): { isSuccess: boolean; message: string } => {
        const imageCountLimit = 4;
        if (files.length > imageCountLimit) {
            return {
                isSuccess: false,
                message: "選択できる画像は最大4枚です。",
            };
        }

        const sizeLimitMb = 6;
        for (let i = 0; i < files.length; i++) {
            const allowExtensions = ".(jpeg|jpg|png|gif)$";
            if (!files[i].name.match(allowExtensions)) {
                return {
                    isSuccess: false,
                    message: "jpeg, jpg, png, gifファイルを選択してください。",
                };
            }

            const sizeLimit = 1024 * 1024 * sizeLimitMb;
            if (files[i].size > sizeLimit) {
                return {
                    isSuccess: false,
                    message: "ファイルサイズは6MB以下にしてください。",
                };
            }
        }

        setNewCommentImages(files);
        return {
            isSuccess: true,
            message: "",
        };
    };

    return {
        memory,
        newCommentText,
        newCommentImages,
        setNewCommentText,
        addComment,
        addFavorite,
        removeFavorite,
        incrementLikedCount,
        setCommentImages,
        tempLikedMemoryIds,
        isLoadingAddComment,
        isMemoryLoading,
        isFavoriteLoading:
            addFavoriteMutation.isPending || removeFavoriteMutation.isPending,
    };
};
